import { router } from '@/router'
import { bindingWechatOpenid, fetchEmailLogin, fetchLogin, registerForGpt } from '@/service'
import { getMembershipInformation } from '@/service/api/basic'
import { useTrackerStore } from '@/store'
import { formatStandardTime, local } from '@/utils'
import { useRouteStore } from './router'
import { useTabStore } from './tab'

interface AuthStatus {
  userInfo: Api.Login.Info | null
  token: string
  openid: string | null
}
export const useAuthStore = defineStore('auth-store', {
  state: (): AuthStatus => {
    return {
      userInfo: local.get('userInfo'),
      token: local.get('accessToken') || '',
      openid: local.get('openid'),
    }
  },
  getters: {
    /** 是否登录 */
    isLogin(state) {
      return Boolean(state.token)
    },
  },
  actions: {
    /* 登录退出，重置用户信息等 */
    async logout() {
      const route = unref(router.currentRoute)
      // 清除本地缓存
      this.clearAuthStorage()
      // 清空路由、菜单等数据
      const routeStore = useRouteStore()
      routeStore.resetRouteStore()
      // 清空标签栏数据
      const tabStore = useTabStore()
      tabStore.clearAllTabs()
      // 重置当前存储库
      this.$reset()
      // 重定向到登录页
      if (route.meta.requiresAuth) {
        router.push({
          name: 'login',
          query: {
            redirect: route.fullPath,
          },
        })
      }
    },
    clearAuthStorage() {
      local.remove('accessToken')
      local.remove('userInfo')
      local.remove('loginShowRemark')
    },

    /* 账号登录 */
    async login(account: string, password: string, captcha: string) {
      try {
        const { code, data } = await fetchLogin({ account, password, captcha })
        if (code !== 1000)
          return

        // 处理登录信息
        await this.handleLoginInfo(data, '账号登录')
      }
      catch (e) {
        console.warn('[Login Error]:', e)
      }
    },
    /* 短信登录 */
    async emailLogin(mobile: string, mobileVerificationCode: string) {
      try {
        const { code, data } = await fetchEmailLogin({ mobile, mobileVerificationCode })
        if (code !== 1000)
          return

        // 处理登录信息
        await this.handleLoginInfo(data, '短信登录')
      }
      catch (e) {
        console.warn('[Login Error]:', e)
      }
    },
    /* 绑定会员号 */
    async bindingWechatOpenidFn(val: any) {
      try {
        const { code, data } = await bindingWechatOpenid(val)
        if (code !== 1000)
          return

        // 处理登录信息
        await this.handleLoginInfo(data, '扫码登录')
      }
      catch (e) {
        console.warn('[Login Error]:', e)
      }
    },
    /* 账号注册 */
    async registerForGptFn(val: any) {
      try {
        const { code, data } = await registerForGpt(val)
        if (code !== 1000)
          return

        // 处理登录信息
        await this.handleLoginInfo(data, '账号登录')
      }
      catch (e) {
        console.warn('[Login Error]:', e)
      }
    },
    /* 获取会员信息 */
    async getMembershipInformationFn() {
      try {
        const { code, data } = await getMembershipInformation()
        if (code !== 1000)
          return
        return data
      }
      catch (e) {
        console.warn('获取会员信息失败:', e)
      }
    },
    /* 处理登录返回的数据 */
    async handleLoginInfo(data: Api.Login.Info, type?: string) {
      const trackerStore = useTrackerStore()

      // 将token和userInfo保存下来
      local.set('userInfo', data)
      local.set('accessToken', data.token)

      trackerStore.initBaseInfo()
      trackerStore.reportBySpecialDirectives('Account_Login', type )

      this.token = data.token
      this.userInfo = data

      // 添加路由和菜单
      const routeStore = useRouteStore()
      await routeStore.initAuthRoute()

      // 进行重定向跳转
      const route = unref(router.currentRoute)
      const query = route.query as { redirect: string }
      router.push({
        path: query.redirect || '/',
      })
    },
  },
})
